import { Table, Button, Modal } from "@renderbus/ui";
import styled from "styled-components";
import { useBillingMessageOperate } from "../hooks/useBillingOperate";
import { useGetBillingMessageTable } from "../hooks/useBillingMessageTable";
import { useEffect, useState } from "react";
import {
  IBillingMessageInfoItem,
  useInitNotification,
} from "../hooks/useInitNotification";
import { useTranslation } from "react-i18next";
import { useNotificationStore } from "../store/notification-store";
import { WarningModalContent } from "@/components/warning-modal-content";
import {
  NotificationModalTip,
  OperateTypeEnum,
  NotificationTabType,
} from "../constants";
import { getOperationType } from "@/components/operation-button/helper/getOperationType";
import { Operation } from "@/components/operation-button";

interface OperateButtonProps {
  isEnable: boolean;
}

const BillingTableWrapper = styled.div`
  position: relative;
  .billing-table .ant-pagination-total-text {
    margin-right: 0;
  }
`;
const OperateButton = styled(Button)<OperateButtonProps>`
  background: transparent;
  color: ${(props) =>
    props.isEnable
      ? "1px solid rgba(255, 255, 255, 0.85)"
      : "rgba(255, 255, 255, 0.35)"};
  border: ${(props) =>
    props.isEnable
      ? "1px solid rgba(255, 255, 255, 0.85)"
      : "rgba(255, 255, 255, 0.35)"};
  margin-right: 18px;
  &:hover,
  &:focus {
    background: transparent;
    color: rgba(255, 255, 255, 0.85);
    border-color: rgba(255, 255, 255, 0.85);
  }
`;

const BiilingOperateButton = styled.div`
  padding-left: 20px;
  position: absolute;
  bottom: 0;
`;

export default function BillingMessage() {
  const { t } = useTranslation("notification");
  const columns = useGetBillingMessageTable();
  const [currentPageNum, setCurrentPageNum] = useState(1);
  const [openTipModal, setOpenTipModal] = useState(false);
  const [billingClickData, setBillingClickData] = useState([] as number[]);
  const [opreateType, setOpreateType] = useState(OperateTypeEnum.read);
  const [operateTip, setOperateTip] = useState("");
  const { billingMessage, fetchNotificationData } = useInitNotification();
  const { setBillingSelectIds, billingSelectIds, currentNotificationTab } =
    useNotificationStore();
  const { handleOperateNotification, handleOpenNotification } =
    useBillingMessageOperate();
  const handleClick = (type: OperateTypeEnum) => {
    setOpenTipModal(true);
    setOpreateType(type);
  };

  useEffect(() => {
    // const isSelectAll
    const item =
      currentNotificationTab === NotificationTabType.BillingMessage
        ? t("notification_billing_message_item")
        : t("notification_appeal_billing_message_item");
    switch (opreateType) {
      case OperateTypeEnum.delete:
        setOperateTip(
          t("notification_billing_operate_delete_choose", { item }) as string
        );
        break;
      case OperateTypeEnum.deleteAll:
        setOperateTip(
          t("notification_billing_operate_delete_all", { item }) as string
        );
        break;
      case OperateTypeEnum.read:
        setOperateTip(
          t("notification_billing_operate_read_choose", { item }) as string
        );
        break;
      case OperateTypeEnum.readAll:
        setOperateTip(
          t("notification_billing_operate_read_all", { item }) as string
        );
        break;
      default:
        break;
    }
  }, [opreateType, currentNotificationTab, t]);

  return (
    <BillingTableWrapper>
      <Table
        dataSource={billingMessage.items}
        columns={columns}
        showQuickJumper={true}
        total={billingMessage.total}
        defaultPageSize={50}
        scroll={{ x: true, y: 540 }}
        paginationCurrent={currentPageNum}
        rowKey={(record: IBillingMessageInfoItem) => record.id}
        rowSelection={{
          type: "checkbox",
          selectedRowKeys: billingSelectIds,
          onSelect: (_, selected, selectedRows) => {
            setBillingSelectIds(selectedRows.map((item) => item.id));
          },
          onSelectAll: (_, selected) =>
            setBillingSelectIds(selected.map((item) => item.id)),
        }}
        onPageNumChange={(key: number) => {
          setCurrentPageNum(key);
          fetchNotificationData(key);
          setBillingSelectIds([]);
        }}
        className="billing-table"
        style={{ padding: "26px 20px 0" }}
        onRow={(row) => {
          return {
            onClick: (e) => {
              const type = getOperationType(e.target as HTMLDivElement);
              if (type !== Operation.Delete) {
                handleOpenNotification(row);
                fetchNotificationData();
                setBillingSelectIds([]);
                setBillingClickData([]);
                return;
              }
              setBillingClickData([row.id]);
              handleClick(OperateTypeEnum.delete);
            },
          };
        }}
      />
      <BiilingOperateButton>
        <OperateButton
          disabled={!billingSelectIds.length}
          isEnable={!!billingSelectIds.length}
          onClick={() => handleClick(OperateTypeEnum.delete)}
        >
          {t("notification_billing_table_delete")}
        </OperateButton>
        <OperateButton
          disabled={!billingSelectIds.length}
          isEnable={!!billingSelectIds.length}
          onClick={() => handleClick(OperateTypeEnum.read)}
        >
          {t("notification_billing_operate_markread")}
        </OperateButton>
        <OperateButton
          isEnable
          onClick={() => handleClick(OperateTypeEnum.readAll)}
        >
          {t("notification_billing_operate_readall")}
        </OperateButton>
        <OperateButton
          isEnable
          onClick={() => handleClick(OperateTypeEnum.deleteAll)}
        >
          {t("notification_billing_operate_deleteall")}
        </OperateButton>
      </BiilingOperateButton>
      <Modal
        open={!!openTipModal}
        closable={false}
        onOk={async () => {
          await handleOperateNotification({
            selectAll: NotificationModalTip[opreateType].selectAll,
            id: NotificationModalTip[opreateType].selectAll
              ? []
              : billingClickData.length
              ? billingClickData
              : billingSelectIds,
            type: opreateType,
          });
          fetchNotificationData();
          setBillingSelectIds([]);
          setBillingClickData([]);
          setOpenTipModal(false);
        }}
        onCancel={() => setOpenTipModal(false)}
      >
        <WarningModalContent
          title={t("notification_billing_operate_delete_title")}
          tip={operateTip}
        />
      </Modal>
    </BillingTableWrapper>
  );
}
